/** Calendar bucketing utility — aggregates events / tasks / deadlines by date
 * for month-view rendering.
 *
 * Time handling:
 *   - Non-all-day events: `startAt` is UTC; convert to browser tz then take date.
 *     A 2026-07-15T01:00:00Z event renders on 2026-07-15 in UTC+8 (09:00 local)
 *     but on 2026-07-14 in UTC-5 (20:00 local previous day). Correct.
 *   - All-day events: skip tz conversion (take UTC date directly). EventInstance
 *     doesn't carry source tz, so UTC date is the closest available approximation.
 *     For law-practice dates (court dates, statutory deadlines) this is correct —
 *     they are anchored to a calendar day, not an instant.
 *   - Tasks/deadlines: `deadlineAt` / `dueDate` carry no time component in
 *     business semantics (lawyer thinks "due on the 15th"), so we take the
 *     UTC date directly without tz conversion.
 */

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import type { CompassItem, EventInstance } from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/** Browser IANA timezone (e.g. "Asia/Shanghai"). Computed once at module load. */
export const BROWSER_TIMEZONE = dayjs.tz.guess();

/** Unified calendar item kind — drives cell color coding + drawer routing. */
export type CalendarItemKind = "event" | "task" | "deadline";

/** Discriminated union: any renderable calendar item, regardless of source. */
export interface CalendarItem {
  kind: CalendarItemKind;
  /** Stable unique key (kind:id) — used as React key and for ListView dedupe. */
  key: string;
  id: string;
  title: string;
  /** ISO datetime used for sorting within a day cell. May be null for unscheduled tasks. */
  sortAt: string | null;
  /** Original source — passed to the detail drawer so it can fetch the right record. */
  source: EventInstance | CompassItem;
}

/** Color bucket label for cell dot rendering + legend. */
export type CalendarItemColor =
  | "event"
  | "event-hearing"
  | "task-urgent"
  | "task-high"
  | "task-normal"
  | "task-low"
  | "deadline"
  | "deadline-statutory";

/**
 * Read a CSS color token at runtime. We don't import the static hex values
 * directly so the palette stays single-sourced in `styles/tokens.css` — the
 * legacy "magic hex string" smell. Tokens are looked up once on module load;
 * if a token is missing (e.g. in a unit-test jsdom environment without CSS
 * custom-property resolution), we fall back to the matching token's legacy
 * hex so tests and bare renders still produce a visible color.
 */
const TOKEN_FALLBACKS: Record<CalendarItemColor, string> = {
  event: "#7C3AED",
  "event-hearing": "#d4380d",
  "task-urgent": "#52c41a",
  "task-high": "#73d13d",
  "task-normal": "#95de64",
  "task-low": "#b7eb8f",
  deadline: "#fa8c16",
  "deadline-statutory": "#cf1322",
};

const TOKEN_NAMES: Record<CalendarItemColor, string> = {
  event: "--jerry-calendar-event",
  "event-hearing": "--jerry-calendar-event-hearing",
  "task-urgent": "--jerry-calendar-task-urgent",
  "task-high": "--jerry-calendar-task-high",
  "task-normal": "--jerry-calendar-task-normal",
  "task-low": "--jerry-calendar-task-low",
  deadline: "--jerry-calendar-deadline",
  "deadline-statutory": "--jerry-calendar-deadline-statutory",
};

function readToken(color: CalendarItemColor): string {
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") {
    return TOKEN_FALLBACKS[color];
  }
  const cssValue = window.getComputedStyle(document.documentElement).getPropertyValue(TOKEN_NAMES[color]).trim();
  return cssValue || TOKEN_FALLBACKS[color];
}

function buildCalendarColors(): Record<CalendarItemColor, string> {
  return {
    event: readToken("event"),
    "event-hearing": readToken("event-hearing"),
    "task-urgent": readToken("task-urgent"),
    "task-high": readToken("task-high"),
    "task-normal": readToken("task-normal"),
    "task-low": readToken("task-low"),
    deadline: readToken("deadline"),
    "deadline-statutory": readToken("deadline-statutory"),
  };
}

/** Color palette — resolved from CSS tokens at module load. */
export const CALENDAR_ITEM_COLORS: Record<CalendarItemColor, string> = buildCalendarColors();

/** Event-type codes that should render with the "hearing" color treatment.
 *  Court hearings are the highest-risk anchor on a law-firm calendar and
 *  deserve a distinct visual even within the generic event bucket. */
const HEARING_EVENT_TYPE_CODES = new Set<string>([
  "law.hearing",
  "law.court_session",
  "law.trial",
]);

/** Map browser date (YYYY-MM-DD) → items. */
export type CalendarBucket = Map<string, CalendarItem[]>;

/** Maximum items rendered in a cell before showing "+N more". */
export const MAX_ITEMS_PER_CELL = 3;

/** Format an ISO datetime as YYYY-MM-DD interpreted in browser tz. */
export function dateKeyFromISO(iso: string, opts?: { allDay?: boolean }): string {
  if (opts?.allDay) {
    return dayjs.utc(iso).format("YYYY-MM-DD");
  }
  return dayjs(iso).tz(BROWSER_TIMEZONE).format("YYYY-MM-DD");
}

/** Format a Dayjs object (from antd Calendar) as YYYY-MM-DD in its own tz
 *  (already localized, no further conversion). Used by MonthView's cellRender. */
export function dateKeyFromDayjs(d: { format: (fmt: string) => string }): string {
  return d.format("YYYY-MM-DD");
}

/** Pick the color bucket for a calendar item. */
export function colorOfItem(item: CalendarItem): CalendarItemColor {
  if (item.kind === "event") {
    const ev = item.source as EventInstance;
    if (ev.eventTypeCode && HEARING_EVENT_TYPE_CODES.has(ev.eventTypeCode)) {
      return "event-hearing";
    }
    return "event";
  }
  if (item.kind === "deadline") {
    const c = item.source as CompassItem;
    return c.isStatutory ? "deadline-statutory" : "deadline";
  }
  const c = item.source as CompassItem;
  switch (c.priority) {
    case "urgent":
      return "task-urgent";
    case "high":
      return "task-high";
    case "low":
      return "task-low";
    default:
      return "task-normal";
  }
}

/** Convert an EventInstance to a CalendarItem. */
function eventToItem(ev: EventInstance): CalendarItem {
  return {
    kind: "event",
    key: `event:${ev.eventId}:${ev.originalStartAt}`,
    id: ev.eventId,
    title: ev.title,
    sortAt: ev.startAt,
    source: ev,
  };
}

/** Convert a CompassItem (task or deadline) to a CalendarItem. */
function compassToItem(c: CompassItem): CalendarItem | null {
  if (c.type === "task") {
    if (!c.deadlineAt) return null;
    return {
      kind: "task",
      key: `task:${c.id}`,
      id: c.id,
      title: c.title,
      sortAt: c.deadlineAt,
      source: c,
    };
  }
  if (c.type === "deadline") {
    if (!c.deadlineAt) return null;
    return {
      kind: "deadline",
      key: `deadline:${c.id}`,
      id: c.id,
      title: c.title,
      sortAt: c.deadlineAt,
      source: c,
    };
  }
  return null;
}

/** Aggregate events + tasks + deadlines into a date-keyed bucket map.
 *
 *  Empty days are absent from the map (cell renderer falls back to empty). */
export function bucketCalendarItems(params: {
  events: EventInstance[];
  tasks: CompassItem[];
  deadlines: CompassItem[];
}): CalendarBucket {
  const { events, tasks, deadlines } = params;
  const bucket: CalendarBucket = new Map();

  const push = (key: string, item: CalendarItem) => {
    const list = bucket.get(key);
    if (list) list.push(item);
    else bucket.set(key, [item]);
  };

  for (const ev of events) {
    const key = dateKeyFromISO(ev.startAt, { allDay: ev.allDay });
    push(key, eventToItem(ev));
  }
  for (const c of [...tasks, ...deadlines]) {
    const item = compassToItem(c);
    if (!item || !item.sortAt) continue;
    const key = dateKeyFromISO(item.sortAt);
    push(key, item);
  }

  // Sort each day's items by sortAt ascending so cell rendering is stable.
  // ISO 8601 strings sort lexicographically == chronologically, so plain
  // string compare is correct and avoids Date parsing overhead.
  for (const list of bucket.values()) {
    list.sort((a, b) => {
      const sa = a.sortAt ?? "￿"; // null sorts last
      const sb = b.sortAt ?? "￿";
      return sa < sb ? -1 : sa > sb ? 1 : 0;
    });
  }

  return bucket;
}

/** Format an ISO datetime for inline display in a cell ("09:00" / "全天" / "—"). */
export function formatCellTime(iso: string | null, allDay?: boolean): string {
  if (!iso) return "—";
  if (allDay) return "全天";
  return dayjs(iso).tz(BROWSER_TIMEZONE).format("HH:mm");
}
